import Styles from './ProfileCreation.module.scss';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useContext } from 'react';
import axios from 'axios';

// component imports
import { useAPI, ViewContext, } from 'components/lib';
import { BasicDetails } from './Forms/BasicDetails';
import { EducationDetails } from './Forms/EducationDetails';
import { WorkDetails } from './Forms/WorkDetails';
import { ExtraDetails } from './Forms/ExtraDetails';
import { ConfirmationCard } from 'components/global/ConfirmationCard/ConfirmationCard';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';



export function ProfileCreation(props) {

    const navigate = useNavigate();
    const { data, loading } = useAPI('/api/hiring/profile');
    const [currentStep, setCurrentStep] = useState(0);
    const [confirmSubmit, setConfirmSubmit] = useState(false);
    const viewContext = useContext(ViewContext);
    let isCreate = data ? data.createProfile : true;

    const [profileData, setProfileData] = useState({});

    const [experience, setExperience] = useState([]);
    const [expValidation, setExpValidation] = useState([]);

    useEffect(() => {
        if (data) {
            setProfileData({ ...data });
            if (data?.masters) {
                setValidation({
                    ...validation,
                    masters: { ...Object.fromEntries(Object.entries(data?.masters).map(([key]) => [key, true])) }
                });
            }
            if (data?.experience) {
                setExperience(data?.experience);
                const expValidationArray = data.experience.map(experience => Object.fromEntries(Object.entries(experience).map(([key]) => [key, true])));
                setExpValidation(expValidationArray);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const [validation, setValidation] = useState({
        first_name: true,
        last_name: true,
        phone_number: true,
        email: true,
        education: true,
        state: true,
        location: true,
        dob: true,
        profile_picture: true,
        high_school_name: true,
        high_school_board: true,
        high_school_start_year: true,
        high_school_end_year: true,
        high_school_percentage: true,
        degree_name: true,
        degree_university: true,
        degree_start_year: true,
        degree_end_year: true,
        degree_percentage: true,
        strengths: true,
        skills: true,
        description: true
    });

    const handleInputChange = (inputValue, field) => {
        // Validation logic for each field
        let isValid = true;
        const currentYear = new Date().getFullYear();
        switch (field) {
            case 'first_name':
                isValid = /^[A-Za-z]+$/.test(inputValue);
                break;
            case 'last_name':
                // Allowing spaces in the last name
                isValid = /^[A-Za-z ]+$/.test(inputValue);
                break;
            case 'phone_number':
                isValid = /^\d{10}$/.test(parseInt(inputValue));
                break;
            case 'email':
                isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputValue);
                break;
            case 'dob':
                const [day, month, year] = inputValue.split('-');
                isValid = /^(\d{2})-(\d{2})-(\d{4})$/.test(inputValue)
                    && !isNaN(new Date(`${year}-${month}-${day}`));
                break;
            case 'degree_end_year':
                if (parseInt(inputValue) <= 1970 || parseInt(inputValue) > currentYear + 2) {
                    isValid = false;
                    break;
                }
                isValid = isValid = (/^\d{4}$/.test(parseInt(inputValue)) || inputValue.toLowerCase() === 'ongoing');;
                break;
            case 'high_school_start_year':
            case 'high_school_end_year':
            case 'degree_start_year':
                if (parseInt(inputValue) <= 1970 || parseInt(inputValue) > currentYear + 2) {
                    isValid = false;
                    break;
                }
                isValid = /^\d{4}$/.test(parseInt(inputValue));
                break;
            case 'high_school_percentage':
            case 'degree_percentage':
                isValid = /^\d{1,2}(\.\d{1,2})?$/.test(parseFloat(inputValue));
                break;
            case 'skills':
                isValid = inputValue?.length !== 0
                break;
            case 'state':
                isValid = inputValue !== null;
                break;
            default:
                isValid = inputValue.trim() !== '';
                break;
        }

        setValidation({ ...validation, [field]: isValid });

        setProfileData({
            ...profileData,
            [field]: Array.isArray(inputValue) ? inputValue : inputValue?.toString(),
        });
    };

    const formsToFill = [
        <BasicDetails
            profileData={profileData}
            handleInputChange={handleInputChange}
            validation={validation}
        />,
        <EducationDetails
            profileData={profileData}
            handleInputChange={handleInputChange}
            validation={validation}
            setProfileData={setProfileData}
            setValidation={setValidation}
        />,
        <WorkDetails
            experience={experience}
            setExperience={setExperience}
            expValidation={expValidation}
            setExpValidation={setExpValidation}
        />,
        <ExtraDetails
            profileData={profileData}
            handleInputChange={handleInputChange}
            validation={validation}
        />
    ]

    const handleCancelClick = () => {
        navigate('/navigate')
    }

    const handleNextClick = () => {
        setCurrentStep(currentStep + 1)
    }

    const handlePrevClick = () => {
        if (currentStep !== 0) {
            setCurrentStep(currentStep - 1)
        }
    }

    const isFieldsEmpty = () => {
        const isDetailsValid = Object.values(validation).includes(false);
        const isDetailsFilled = Object.values(profileData).includes(null) || Object.values(profileData).includes("");
        return isDetailsValid || isDetailsFilled
    }

    const handleFromSubmit = async () => {
        const options = {
            method: 'POST',
            url: `/api/hiring/profile`,
            data: [profileData, experience],
        };
        await axios.request(options)
            .then(function (response) {
                navigate('/hiring/resume')
            })
            .catch((err) => {
                viewContext.notification.show('Error creating profile', 'error', true);
            });
    }

    const handleFromEdit = async () => {
        const options = {
            method: 'PATCH',
            url: `/api/hiring/profile`,
            data: [profileData, experience],
        };
        await axios.request(options)
            .then(function (response) {
                navigate('/hiring/resume')
            }).catch((err) => {
                viewContext.notification.show('Error editing profile', 'error', true);
            });
    }

    return (
        <>
            {loading ? (
                <div>
                    <LoadingBackdrop isOpen={true} />
                </div>
            ) : <>
                <ConfirmationCard
                    title={'Create Profile?'}
                    body="I hereby confirm that all the information provided in this resume is true and accurate to the best of my knowledge. I understand that any misrepresentation or omission of facts may lead to disqualification from consideration or termination if already employed."
                    showCard={confirmSubmit}
                    setShowCard={setConfirmSubmit}
                    handleConfirm={isCreate ? handleFromSubmit : handleFromEdit}
                />
                <div id='ancestorContainer' className={Styles.ancestorContainer}>
                    <span>
                        <p id='heading' className={Styles.heading}>
                            {isCreate ? 'Create Your Hiring Profile' : 'Edit Your Hiring Profile'}
                        </p>
                        <p id='description' className={Styles.description}>
                            Before proceeding to Apply for jobs, please finish {isCreate ? 'create' : 'edit'} your hiring profile by filling in some basic information!
                        </p>
                    </span>
                    <div id='profileButtons' className={Styles.profileButtons}>
                        <button
                            id='cancelButton'
                            onClick={handleCancelClick}
                            className={Styles.cancelButton}
                        >
                            Cancel
                        </button>
                        <button
                            id='nextButton'
                            onClick={handlePrevClick}
                            className={Styles.nextButton}
                            disabled={currentStep === 0}
                        >
                            Prev
                        </button>
                        <button
                            id='nextButton'
                            onClick={() => {
                                if (currentStep <= formsToFill.length - 2) {
                                    handleNextClick()
                                }
                                if (currentStep === formsToFill.length - 1) {
                                    setConfirmSubmit(true)
                                }
                            }}
                            className={Styles.nextButton}
                            disabled={currentStep === formsToFill.length - 1 &&
                                isFieldsEmpty()}
                        >
                            {currentStep === formsToFill.length - 1 ?
                                'Finish'
                                : 'Next'
                            }
                        </button>
                    </div>
                    {formsToFill[currentStep]}
                </div>
            </>}
        </>
    )
}

